import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, firestore } from '../views/firebaseConfig';
import { COLLECTIONS, evaluateProfileCompletion } from '../contracts/p4r';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (nextUser) => {
    setUser(nextUser);
    setLoading(false);
    if (!nextUser) {
      setProfile(null);
      setProfileLoading(false);
    }
  }), []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setProfileLoading(true);
    return onSnapshot(
      doc(firestore, COLLECTIONS.USER_PROFILES, user.uid),
      (snapshot) => {
        setProfile(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
        setProfileLoading(false);
      },
      (error) => {
        console.error('Profile listener failed:', error);
        setProfile(null);
        setProfileLoading(false);
      }
    );
  }, [user?.uid]);

  const completion = useMemo(() => evaluateProfileCompletion(profile), [profile]);
  const value = useMemo(() => ({
    user,
    loading,
    profile,
    profileLoading,
    profileComplete: completion.isComplete,
    missingProfileFields: completion.missingFields,
    isAnonymous: Boolean(user?.isAnonymous),
  }), [user, loading, profile, profileLoading, completion]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
